import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import configuration from "react-global-configuration";
import { getShopPageConfig } from "../Helper/siteContent";
import {
  isExternalHref,
  normalizeExternalUrl,
} from "../Helper/urls";
import DestinationExperienceGate from "../Experience/DestinationExperienceGate";
import ShopCatalog from "./ShopCatalog";
import "./ShopReserved.css";

const MOBILE_MQ = "(max-width: 767.98px)";
const TABLET_MQ = "(min-width: 768px) and (max-width: 1023.98px)";
const FADE_MS = 900;
/** readyState >= HAVE_FUTURE_DATA */
const VIDEO_READY_STATE = 3;

function ShopCta({ label, url, className }) {
  if (!label) {
    return null;
  }
  const href = (url || "").trim() || "/home";
  if (isExternalHref(href)) {
    return (
      <a
        className={className}
        href={normalizeExternalUrl(href)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {label}
    </Link>
  );
}

function getDeviceClass() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "desktop";
  }
  if (window.matchMedia(MOBILE_MQ).matches) {
    return "mobile";
  }
  if (window.matchMedia(TABLET_MQ).matches) {
    return "tablet";
  }
  return "desktop";
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Resolve which still image URL (if any) applies for the current device.
 * Desktop/tablet → desktop image when enabled.
 * Mobile → mobile image when enabled (falls back to desktop URL if mobile URL empty).
 */
function resolveImageForDevice(page, device) {
  const desktopUrl = (page.hero_image || "").trim();
  const mobileUrl = (page.hero_image_mobile || "").trim();

  if (device === "mobile") {
    if (page.hero_image_mobile_enabled) {
      if (mobileUrl) return mobileUrl;
      if (desktopUrl) return desktopUrl;
    }
    return "";
  }

  if (page.hero_image_enabled && desktopUrl) {
    return desktopUrl;
  }
  return "";
}

function shouldPlayVideoOnDevice(page, device) {
  if (!page.hero_video_enabled || !(page.hero_video || "").trim()) {
    return false;
  }
  if (prefersReducedMotion()) {
    return false;
  }
  const playOn = page.hero_video_play_on || {};
  if (device === "mobile") return !!playOn.mobile;
  if (device === "tablet") return !!playOn.tablet;
  return !!playOn.desktop;
}

function isVideoReady(videoEl) {
  return !!(videoEl && videoEl.readyState >= VIDEO_READY_STATE);
}

/**
 * Wait until the video can paint a frame (and preferably is playing).
 * Resolves immediately if already ready.
 */
function waitForVideoPlaybackReady(videoEl) {
  return new Promise((resolve) => {
    if (!videoEl) {
      resolve(false);
      return;
    }

    let settled = false;
    let frameHandle = null;

    const cleanup = () => {
      videoEl.removeEventListener("canplay", onReady);
      videoEl.removeEventListener("canplaythrough", onReady);
      videoEl.removeEventListener("loadeddata", onReady);
      videoEl.removeEventListener("playing", onReady);
      if (
        frameHandle != null &&
        typeof videoEl.cancelVideoFrameCallback === "function"
      ) {
        try {
          videoEl.cancelVideoFrameCallback(frameHandle);
        } catch (e) {
          // ignore
        }
      }
    };

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ok);
    };

    const onReady = () => {
      if (isVideoReady(videoEl)) {
        finish(true);
      }
    };

    videoEl.addEventListener("canplay", onReady);
    videoEl.addEventListener("canplaythrough", onReady);
    videoEl.addEventListener("loadeddata", onReady);
    videoEl.addEventListener("playing", onReady);

    if (typeof videoEl.requestVideoFrameCallback === "function") {
      frameHandle = videoEl.requestVideoFrameCallback(() => finish(true));
    }

    if (isVideoReady(videoEl)) {
      onReady();
    }
  });
}

/**
 * A-006.1–A-006.3C — Shop landing with cinematic image→video crossfade.
 * Branding uses configurable site_name; portrait media uses object-fit contain.
 */
function ShopReserved() {
  const page = getShopPageConfig();
  const siteName = configuration.get("configData.site_name") || "Shop";
  const seoTitle = page.seo_title || page.hero_title || page.title || "Shop";
  const documentTitle = `${seoTitle} | ${siteName}`;
  const seoDescription =
    page.seo_description || page.hero_description || page.description || "";
  const alignment = page.hero_background_alignment || "center";
  const overlay = page.hero_overlay_opacity;

  const [device, setDevice] = useState(() => getDeviceClass());
  // image | armed (video ready under image) | transitioning | video
  const [phase, setPhase] = useState("image");
  const videoRef = useRef(null);
  const timersRef = useRef({ hold: null, fade: null });
  const cancelledRef = useRef(false);

  const imageUrl = resolveImageForDevice(page, device);
  const videoUrl = (page.hero_video || "").trim();
  const videoOnDevice = shouldPlayVideoOnDevice(page, device);
  const hasImage = !!imageUrl;
  const hasVideo = videoOnDevice && !!videoUrl;
  const durationSec = Number.isFinite(page.hero_image_display_duration)
    ? Math.max(0, Math.min(30, page.hero_image_display_duration))
    : 3;
  const hasMedia = hasImage || hasVideo;

  const mediaStyle = {
    objectPosition: alignment,
    backgroundPosition: alignment,
  };

  const clearTimers = () => {
    if (timersRef.current.hold) {
      clearTimeout(timersRef.current.hold);
      timersRef.current.hold = null;
    }
    if (timersRef.current.fade) {
      clearTimeout(timersRef.current.fade);
      timersRef.current.fade = null;
    }
  };

  useEffect(() => {
    const update = () => setDevice(getDeviceClass());
    update();
    const mobileMql = window.matchMedia(MOBILE_MQ);
    const tabletMql = window.matchMedia(TABLET_MQ);
    const add = (mql) => {
      if (mql.addEventListener) mql.addEventListener("change", update);
      else if (mql.addListener) mql.addListener(update);
    };
    const remove = (mql) => {
      if (mql.removeEventListener) mql.removeEventListener("change", update);
      else if (mql.removeListener) mql.removeListener(update);
    };
    add(mobileMql);
    add(tabletMql);
    return () => {
      remove(mobileMql);
      remove(tabletMql);
    };
  }, []);

  /**
   * Cinematic crossfade orchestration:
   * 1) Show image (covers everything)
   * 2) Preload video fully hidden under the image
   * 3) After duration AND video ready → start playback while still covered
   * 4) Arm video (opacity 1 under image) → fade image out → then hide image
   */
  useEffect(() => {
    cancelledRef.current = false;
    clearTimers();

    if (!hasVideo) {
      setPhase("image");
      return () => {
        cancelledRef.current = true;
        clearTimers();
      };
    }

    if (!hasImage) {
      setPhase("video");
      const videoEl = videoRef.current;
      if (videoEl && page.hero_video_autoplay) {
        videoEl.muted = !!page.hero_video_muted;
        videoEl.loop = !!page.hero_video_loop;
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }
      return () => {
        cancelledRef.current = true;
        clearTimers();
      };
    }

    setPhase("image");
    const videoEl = videoRef.current;
    if (videoEl) {
      try {
        videoEl.pause();
        videoEl.currentTime = 0;
      } catch (e) {
        // ignore seek errors while metadata loads
      }
      videoEl.muted = !!page.hero_video_muted;
      videoEl.loop = !!page.hero_video_loop;
      // Kick off network preload while hidden.
      try {
        videoEl.load();
      } catch (e) {
        // ignore
      }
      videoEl.preload = "auto";
    }

    const holdMs = durationSec * 1000;
    const holdStartedAt = Date.now();

    const beginCrossfade = async () => {
      if (cancelledRef.current) return;
      const el = videoRef.current;
      if (!el) return;

      // Keep showing image until video can actually play.
      await waitForVideoPlaybackReady(el);
      if (cancelledRef.current) return;

      // Ensure hold duration has fully elapsed (timer may have fired early
      // relative to buffering; re-check remaining time).
      const elapsed = Date.now() - holdStartedAt;
      const remaining = Math.max(0, holdMs - elapsed);
      if (remaining > 0) {
        await new Promise((r) => {
          timersRef.current.hold = setTimeout(r, remaining);
        });
        timersRef.current.hold = null;
        if (cancelledRef.current) return;
      }

      // Start playback while still fully covered by the opaque image.
      el.muted = !!page.hero_video_muted;
      el.loop = !!page.hero_video_loop;
      if (page.hero_video_autoplay) {
        try {
          const playPromise = el.play();
          if (playPromise && typeof playPromise.then === "function") {
            await playPromise.catch(() => {});
          }
        } catch (e) {
          // Autoplay blocked — still attempt visual reveal of paused first frame.
        }
      }

      await waitForVideoPlaybackReady(el);
      if (cancelledRef.current) return;

      // Video is armed underneath (opacity 1, z below image). Fade image away.
      setPhase("armed");
      // Next frame: start CSS fade so armed styles apply first.
      requestAnimationFrame(() => {
        if (cancelledRef.current) return;
        setPhase("transitioning");
        timersRef.current.fade = setTimeout(() => {
          if (cancelledRef.current) return;
          setPhase("video");
          timersRef.current.fade = null;
        }, FADE_MS);
      });
    };

    // After hold duration, attempt crossfade (will wait for ready if needed).
    timersRef.current.hold = setTimeout(() => {
      timersRef.current.hold = null;
      beginCrossfade();
    }, holdMs);

    // Also preload readiness in parallel — if duration is 0, beginCrossfade
    // waits for ready then transitions immediately.
    if (durationSec === 0) {
      clearTimeout(timersRef.current.hold);
      timersRef.current.hold = null;
      beginCrossfade();
    }

    return () => {
      cancelledRef.current = true;
      clearTimers();
    };
  }, [
    device,
    hasImage,
    hasVideo,
    durationSec,
    videoUrl,
    page.hero_video_autoplay,
    page.hero_video_muted,
    page.hero_video_loop,
  ]);

  // Keep image mounted through the entire fade; only fully hide after complete.
  const imageFading = phase === "transitioning";
  const videoArmed =
    hasVideo &&
    (phase === "armed" || phase === "transitioning" || phase === "video" || !hasImage);
  const videoPreloading = hasVideo && phase === "image" && hasImage;

  const imageClassName = [
    "shop-reserved__image",
    imageFading ? "shop-reserved__image--fading-out" : "",
    phase === "video" ? "shop-reserved__image--hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const videoClassName = [
    "shop-reserved__video",
    videoPreloading ? "shop-reserved__video--preloading" : "",
    videoArmed ? "shop-reserved__video--armed" : "",
    phase === "video" || (!hasImage && hasVideo)
      ? "shop-reserved__video--active"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <DestinationExperienceGate destinationType="shop">
    <>
      <div
        className={`shop-reserved${hasMedia ? " shop-reserved--has-media" : ""}`}
      >
        <Helmet>
          <title>{documentTitle}</title>
          {seoDescription ? (
            <meta name="description" content={seoDescription} />
          ) : null}
          <meta property="og:title" content={seoTitle} />
          {seoDescription ? (
            <meta property="og:description" content={seoDescription} />
          ) : null}
          {page.hero_image ? (
            <meta property="og:image" content={page.hero_image} />
          ) : null}
        </Helmet>

        <div className="shop-reserved__media" aria-hidden="true">
          {hasImage ? (
            <img
              className={imageClassName}
              src={imageUrl}
              alt=""
              style={mediaStyle}
            />
          ) : null}

          {hasVideo ? (
            <video
              ref={videoRef}
              className={videoClassName}
              muted={!!page.hero_video_muted}
              loop={!!page.hero_video_loop}
              playsInline={!!page.hero_video_playsinline}
              preload="auto"
              // No poster during image→video crossfade — poster can flash a still
              // that differs from the first decoded frame.
              poster={!hasImage ? imageUrl || undefined : undefined}
              style={mediaStyle}
            >
              <source src={videoUrl} />
            </video>
          ) : null}

          <div
            className="shop-reserved__overlay"
            style={{ opacity: Number.isFinite(overlay) ? overlay : 0.55 }}
          />
        </div>

        <div className="shop-reserved__inner">
          <h1 className="shop-reserved__title">
            {page.hero_title}
          </h1>
          {page.hero_subtitle ? (
            <p className="shop-reserved__subtitle">
              {page.hero_subtitle}
            </p>
          ) : null}
          {page.hero_description ? (
            <p className="shop-reserved__description">
              {page.hero_description}
            </p>
          ) : null}
          <div className="shop-reserved__actions">
            <ShopCta
              label={page.primary_cta_label}
              url={page.primary_cta_url}
              className="btn btn-danger shop-reserved__cta"
            />
            {page.secondary_cta_label && page.secondary_cta_url ? (
              <ShopCta
                label={page.secondary_cta_label}
                url={page.secondary_cta_url}
                className="btn btn-outline-light shop-reserved__cta shop-reserved__cta--secondary"
              />
            ) : null}
          </div>
        </div>
      </div>
      <ShopCatalog />
    </>
    </DestinationExperienceGate>
  );
}

export default ShopReserved;
